import {
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  Icon,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from 'src/components/Button';
import { BUTTON_COLOR, BUTTON_VARIANT } from 'src/components/Button/types';
import colors from 'src/theme/base/colors';
import { AddFacebookGroup, ScheduleFacebookPostStep3Props } from './types';
import boxShadows from 'src/theme/base/boxShadows';
import { Input } from 'src/components/Input';
import { TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { PageOptions } from 'src/DTO/FacebookPost.type';
import FacebookLogin, {
  FacebookLoginClient,
  LoginResponse,
  SuccessResponse,
} from '@greatsumini/react-facebook-login';
import { handleGroupSelection, handlePageSelection } from 'src/utils/facebookPostUtils';
import { facebookConfig } from 'src/configs/config';
import {
  isDuplicateGroupName,
  isDuplicateGroupURL,
  isValidFBGroupName,
  isValidFBGroupURL,
} from 'src/utils/validateFacebookGroups';
import { dialogPaperStyles } from 'src/components/PasswordReset/styles';
import { FacebookGroupData, FacebookGroupPayload } from 'src/api/hooks/useFacebookPost.types';
import { PageGroupOptions } from './constants';

export const ScheduleFacebookPostStep3: React.FC<ScheduleFacebookPostStep3Props> = ({
  onSubmit,
  handlePreviousButtonClick,
  postSchedule,
  handleMoveToNextStep,
  pageOptions,
  groupOptions,
  pageData,
  setPageData,
  groupData,
  setGroupData,
  handleAuthSuccess,
  authFacebookPagesLoading,
  getConnectedFacebookPagesLoading,
  setIsLogged,
  isLogged,
  populatedPageData,
  jobId,
  populatedGroupData,
  addFacebookGroups,
}) => {
  const { t } = useTranslation();

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState<boolean>(false);

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const selectedPages = pageData.map(data => data.name);
  const selectedGroups = groupData.map(data => data.name);

  const allGroupUrls = [
    ...Object.values(PageGroupOptions),
    ...populatedGroupData.map(item => item.url),
  ];

  const [facebookGroup, setFacebookGroup] = useState<AddFacebookGroup>({
    groupName: '',
    groupURL: '',
    errors: {
      groupName: false,
      groupURL: false,
    },
  });

  const { groupName, groupURL, errors } = facebookGroup;

  const hasGroupValidationErrors = Object.values(errors).some(error => error === true);

  const handleSuccessLogin = () => {
    FacebookLoginClient.getLoginStatus((res: LoginResponse) => {
      if (res.status === 'connected') {
        setIsLogged(true);
      } else {
        setIsLogged(false);
      }
    });
  };

  const responseFacebook = (response: SuccessResponse) => {
    if (response.accessToken) {
      handleAuthSuccess(response.accessToken);
    }
  };

  const handleClickOpen = () => {
    setIsDialogOpen(true);
  };

  const handlePageChange = (event: SelectChangeEvent<string[]>) => {
    handlePageSelection(event, populatedPageData, PageOptions.DELLA_DIAKMUNKA, setPageData);
  };

  const handleGroupChange = (event: SelectChangeEvent<string[]>) => {
    handleGroupSelection(event, populatedGroupData, setGroupData);
  };

  const handleClose = () => {
    setFacebookGroup({
      groupName: '',
      groupURL: '',
      errors: { groupName: false, groupURL: false },
    });
    setIsConfirmDialogOpen(false);
    setIsDialogOpen(false);
  };

  const handleGroupURLChange = (e: React.FormEvent<HTMLInputElement>) => {
    const url = e.currentTarget.value;
    setFacebookGroup(prev => ({
      ...prev,
      groupURL: url,
      errors: {
        ...prev.errors,
        groupURL: !isValidFBGroupURL(url) || isDuplicateGroupURL(url, allGroupUrls),
      },
    }));
  };

  const handleGroupNameChange = (e: React.FormEvent<HTMLInputElement>) => {
    const name = e.currentTarget.value;
    setFacebookGroup(prev => ({
      ...prev,
      groupName: name,
      errors: {
        ...prev.errors,
        groupName: !isValidFBGroupName(name) || isDuplicateGroupName(name, populatedGroupData),
      },
    }));
  };

  const handleShowCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(true);
  };

  const handleSubmit = () => {
    onSubmit({
      ...postSchedule,
      pageData,
      groupData,
    });
    handleMoveToNextStep();
  };

  const handleSave = () => {
    if (hasGroupValidationErrors || !jobId) return;

    const groupPayload: FacebookGroupPayload = { name: groupName, url: groupURL, jobId };

    addFacebookGroups({ variables: groupPayload });

    const newGroup: FacebookGroupData = { name: groupName, url: groupURL };

    setGroupData(prev => [...prev, newGroup]);

    handleClose();
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box mb={4} display="flex" justifyContent="center" alignItems="center">
          <Typography variant="h5">
            <Trans>Add meg hogy hova posztoljunk</Trans>
          </Typography>
        </Box>
        <Box
          mb={3}
          p={2}
          border={`1px solid ${colors.inputColors.borderColor.main}`}
          borderRadius={2}
          boxShadow={boxShadows.sm}
          bgcolor="white">
          <Typography variant="h6" align="center" gutterBottom>
            <Trans>Mely oldalakra posztoljunk?</Trans>
          </Typography>
          <FormControl fullWidth variant="outlined" size="small" sx={{ width: '100%' }}>
            {authFacebookPagesLoading || getConnectedFacebookPagesLoading ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <Select
                input={
                  <Input
                    icon={{
                      component: <Icon>expand_more</Icon>,
                      direction: 'right',
                    }}
                  />
                }
                multiple
                sx={{ width: '100%' }}
                value={selectedPages}
                onChange={e => handlePageChange(e)}
                renderValue={selected => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {selected?.map(value => {
                      const page = pageData.find(page => page?.name === value);
                      return (
                        <Tooltip
                          placement="top"
                          enterDelay={100}
                          leaveDelay={300}
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: 'rgba(122, 191, 193, 0.5)',
                                color: 'white',
                                borderRadius: '8px',
                                padding: '8px 16px',
                                backdropFilter: 'blur(15px)',
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                                maxWidth: '400px',
                              },
                            },
                          }}
                          key={value}
                          title={
                            <Typography
                              variant="h6"
                              fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}
                              component={Link}
                              href={page?.url}
                              target="_blank"
                              rel="noopener noreferrer">
                              {page?.url || 'Nincs megadva URL'}
                            </Typography>
                          }
                          arrow>
                          <Chip
                            sx={{
                              padding: 2,
                              margin: 1,
                              '&:hover': {
                                backgroundColor: '#17c1e8',
                              },
                            }}
                            label={value}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}>
                {pageOptions?.map(option => (
                  <MenuItem key={option} value={option}>
                    <Checkbox checked={selectedPages?.includes(option)} />
                    {option}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
          <Box mt={2}>
            <FacebookLogin
              appId={facebookConfig.appId}
              scope="pages_manage_posts, pages_show_list"
              onSuccess={response => {
                responseFacebook(response);
                handleSuccessLogin();
              }}
              render={renderProps => (
                <Button
                  disabled={isLogged}
                  variant={BUTTON_VARIANT.GRADIENT}
                  color={BUTTON_COLOR.DARK}
                  onClick={renderProps.onClick}>
                  {isLogged ? <Trans>facebookAuth.loggedIn</Trans> : <Trans>generic.add</Trans>}
                </Button>
              )}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          mb={3}
          p={2}
          border={`1px solid ${colors.inputColors.borderColor.main}`}
          borderRadius={2}
          boxShadow={boxShadows.sm}
          bgcolor="white">
          <Typography variant="h6" align="center" gutterBottom>
            <Trans>Mely csoportokba posztoljunk?</Trans>
          </Typography>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              input={
                <Input
                  icon={{
                    component: <Icon>expand_more</Icon>,
                    direction: 'right',
                  }}
                />
              }
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 250,
                  },
                },
              }}
              multiple
              sx={{ width: '100%' }}
              value={selectedGroups}
              onChange={handleGroupChange}
              renderValue={selected => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {selected?.map(value => {
                    const group = groupData?.find(group => group?.name === value);
                    return (
                      <Tooltip
                        placement="top"
                        enterDelay={100}
                        leaveDelay={300}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: 'rgba(122, 191, 193, 0.5)',
                              color: 'white',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              backdropFilter: 'blur(15px)',
                              boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                              maxWidth: '400px',
                            },
                          },
                        }}
                        key={value}
                        title={
                          <Typography
                            variant="h6"
                            fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}
                            component={Link}
                            href={group?.url}
                            target="_blank"
                            rel="noopener noreferrer">
                            {group?.url || 'Nincs megadva URL'}
                          </Typography>
                        }
                        arrow>
                        <Chip
                          sx={{
                            padding: 2,
                            margin: 1,
                            '&:hover': {
                              backgroundColor: '#17c1e8',
                            },
                          }}
                          label={value}
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
              )}>
              {groupOptions?.map(option => (
                <MenuItem key={option} value={option}>
                  <Checkbox checked={selectedGroups?.includes(option)} />
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box mt={2}>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}
              onClick={() => {
                handleClickOpen();
              }}>
              <Trans>generic.add</Trans>
            </Button>
          </Box>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between">
          <Button onClick={handlePreviousButtonClick}>
            <Trans>generic.back</Trans>
          </Button>
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            type="submit"
            disabled={!selectedPages.length || !selectedGroups.length}
            onClick={handleSubmit}>
            <Trans>jobPage.createRoleForm.submitButtonLabel</Trans>
          </Button>
        </Box>
      </Grid>

      <Dialog
        keepMounted
        open={isDialogOpen}
        onClose={groupName || groupURL ? handleShowCloseConfirmDialog : handleClose}
        PaperProps={{ style: { ...dialogPaperStyles, padding: '10px' } }}>
        <DialogTitle>
          <Box display="flex" justifyContent="center" sx={{ padding: '0.5rem' }}>
            <Typography fontWeight={TYPOGRAPHY_WEIGHT.REGULAR} sx={{ fontSize: 19 }}>
              <Trans>facebookAI.addGroupModal.title</Trans>
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={3}>
            <Input
              error={errors.groupName}
              inputLabel={t('facebookAI.addGroupModal.name')}
              placeholder={t('facebookAI.addGroupModal.namePlaceholder')}
              value={groupName}
              helperText={
                errors.groupName && groupName
                  ? !isValidFBGroupName(groupName)
                    ? t('validation.invalidFbGroupName')
                    : t('validation.FbGroupNameAlreadyExists')
                  : undefined
              }
              onChange={handleGroupNameChange}
            />
          </Box>

          <Box mb={1}>
            <Input
              error={errors.groupURL}
              inputLabel={t('facebookAI.addGroupModal.url')}
              type="url"
              placeholder={t('facebookAI.addGroupModal.urlPlaceholder')}
              value={groupURL}
              helperText={
                errors.groupURL && groupURL
                  ? !isValidFBGroupURL(groupURL)
                    ? t('validation.invalidFbGroupURL')
                    : t('validation.FbGroupURLAlreadyExists')
                  : undefined
              }
              onChange={handleGroupURLChange}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            <Trans>generic.no</Trans>
          </Button>
          <Button
            variant={BUTTON_VARIANT.GRADIENT}
            color={BUTTON_COLOR.DARK}
            onClick={handleSave}
            disabled={!groupURL || !groupName || hasGroupValidationErrors}>
            Mentés
          </Button>
        </DialogActions>
        <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
          <DialogTitle>
            <Trans>facebookAI.addGroupModal.action.confirmCloseTitle</Trans>
          </DialogTitle>
          <DialogContent sx={{ width: 400, padding: '1rem', margin: '1rem' }}>
            <DialogContentText color="textPrimary">
              <Trans>facebookAI.addGroupModal.action.confirmCloseText</Trans>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsConfirmDialogOpen(false)}>
              <Trans>generic.no</Trans>
            </Button>
            <Button
              onClick={handleClose}
              variant={BUTTON_VARIANT.GRADIENT}
              color={BUTTON_COLOR.DARK}>
              <Trans>generic.yes</Trans>
            </Button>
          </DialogActions>
        </Dialog>
      </Dialog>
    </Grid>
  );
};
